import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { UniversalContact } from '@/hooks/useUniversalContacts';
import { useFamily } from '@/hooks/useFamily';

interface MultipleAssigneeAvatarsProps {
  assignedContacts: UniversalContact[];
  size?: 'sm' | 'md' | 'lg';
  maxVisible?: number;
}

export function MultipleAssigneeAvatars({ 
  assignedContacts, 
  size = 'sm', 
  maxVisible = 3 
}: MultipleAssigneeAvatarsProps) {
  const { profile } = useFamily();
  
  // Filter out current user's avatar - only show others
  const filteredContacts = assignedContacts.filter(contact => contact.profile_id !== profile?.id);
  
  if (filteredContacts.length === 0) return null;

  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8', 
    lg: 'h-10 w-10'
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const getInitials = (contact: UniversalContact) => {
    return contact.displayName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const visibleContacts = filteredContacts.slice(0, maxVisible);
  const remainingCount = filteredContacts.length - maxVisible;

  if (filteredContacts.length === 1) {
    // Single avatar - same as before
    const contact = filteredContacts[0];
    return (
      <Avatar className={sizeClasses[size]}>
        <AvatarImage src={contact.avatar_url} />
        <AvatarFallback className={textSizeClasses[size]}>
          {getInitials(contact)}
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <div className="flex items-center -space-x-1">
      {visibleContacts.map((contact, index) => (
        <Avatar 
          key={contact.id} 
          className={`${sizeClasses[size]} border-2 border-background relative`}
          style={{ zIndex: visibleContacts.length - index }}
        >
          <AvatarImage src={contact.avatar_url} />
          <AvatarFallback className={textSizeClasses[size]}>
            {getInitials(contact)}
          </AvatarFallback>
        </Avatar>
      ))}
      
      {remainingCount > 0 && (
        <Badge 
          variant="secondary" 
          className={`${size === 'sm' ? 'h-6 text-xs px-1.5' : 'h-8 text-sm px-2'} ml-1 bg-muted/80`}
        >
          +{remainingCount}
        </Badge>
      )}
    </div>
  );
}